// Cleans a chat message before it leaves the user's device. Messages must be
// plain English: links, code, queries, symbols and prompt-injection phrases
// are refused, and every data value is replaced by a placeholder the AI
// never sees the value of:
//   quoted text, emails, dates, numbers, ids  -> {{text_1}}, {{date_1}}, {{number_1}}
//   words that aren't plain English           -> {{text_2}}   ("umair")
//   capitalised words mid-sentence             -> {{text_3}}   ("Mark")
//   words found in the user's files            -> {{text_4}}   ("paid", when a cell says "Paid")
// The workflow's own column names and letters are kept. The real values stay
// in a vault on the device and fill in the step values the AI declares with
// the placeholder's name. The rules and vocabulary come from the server
// (GET /ai/text-rules), which checks every message again the same way.

// Invisible, control and direction-changing characters (used to hide text).
// eslint-disable-next-line no-control-regex
const INVISIBLE = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u00AD\u034F\u061C\u115F\u1160\u17B4\u17B5\u180B-\u180F\u200B-\u200F\u202A-\u202E\u2060-\u206F\u3164\uFE00-\uFE0F\uFEFF\uFFA0\uFFF0-\uFFFB]/g;

// Same as normalize() in server/ai/plainText.js.
export const normalize = (text) =>
  String(text ?? "")
    .normalize("NFKC")
    .replace(INVISIBLE, "")
    .replace(/[\u2018\u2019\u201A\u201B\u2032\u02BC]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F\u2033]/g, '"')
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/\r\n?/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

// Same as nameWords() in server/ai/plainText.js.
export const nameWords = (names) => {
  const words = new Set();
  names.forEach((name) => {
    const add = (part) => part && words.add(part.toLowerCase());
    String(name ?? "")
      .split(/[^A-Za-z0-9_]+/)
      .forEach((part) => {
        add(part);
        part.split("_").forEach(add);
        part.split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Za-z])(?=\d)|(?<=\d)(?=[A-Za-z])/).forEach(add);
      });
  });
  return words;
};

// Compiled rules from GET /ai/text-rules.
export const compileRules = ({ maxChars, blocked, allowedChars, messages, words }) => ({
  maxChars,
  messages,
  blocked: blocked.map(({ reason, source, flags }) => ({ reason, pattern: new RegExp(source, flags) })),
  allowedChars: new RegExp(allowedChars),
  vocabulary: new Set(words.split("\n").filter(Boolean)),
});

// The first blocking rule the text breaks: { reason, rule } with the rule
// named as the server names it ("system:2" = the third system rule), or null.
const blockedRule = (rules, text) => {
  const index = rules.blocked.findIndex(({ pattern }) => pattern.test(text));
  if (index === -1) return null;
  const { reason } = rules.blocked[index];
  return { reason, rule: `${reason}:${rules.blocked.slice(0, index).filter((r) => r.reason === reason).length}` };
};
const blockedReason = (rules, text) => blockedRule(rules, text)?.reason || null;

// Words that are never treated as data, even when a cell holds them or they
// are capitalised mid-sentence: function words and the vocabulary of
// describing a transformation.
const SAFE_WORDS = new Set(
  `a an the and or but nor not no yes if then else when where which who whom whose what why how that this these those
it its it's is are was were be been being am do does did done has have had having would shall should can could
might must of in on at to from by for with without into onto over under above below between among per via than
as so too very more most less least each every all any some none both either neither other another same such only
just also again once i me my we us our you your they them their he she him her his hers one first last next previous
new old up down out off about after before during until while within across along around through there here
row rows column columns cell cells table tables sheet sheets file files source sources step steps output outputs
value values header headers name names data list lists total totals sum sums count counts average min minimum
maximum add adds added remove removes removed delete deleted keep keeps kept filter filters filtered sort sorts sorted
order ordered group grouped grouping split splits merge merged join joined combine combined convert converted
change changed replace replaced rename renamed copy copied move moved fill filled show shows create make making
calculate calculated compute set get use using find match matches matching contain contains containing include
includes including exclude excludes excluding equal equals greater less smaller larger bigger higher lower between
blank empty missing duplicate duplicates unique distinct number numbers text date dates day days week weeks month
months year years today yesterday tomorrow time amount amounts price prices quantity rate percent percentage
ascending descending top bottom left right start end begin ending beginning format formatted round rounded decimal
decimals digits uppercase lowercase trim spaces space comma commas dot dash new line lines true false yes no
please thanks thank want need like would only always never same different whole full part`.split(/\s+/),
);

// Words that are fine capitalised anywhere (acronyms and "I").
const CAPS_OK = new Set(["i", "i'm", "i've", "i'd", "i'll", "csv", "xlsx", "xls", "tsv", "id", "ids", "ok"]);

const MONTHS = "jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?";
const FULL_MONTHS = "january|february|march|april|june|july|august|september|october|november|december";
const WEEKDAYS = "monday|tuesday|wednesday|thursday|friday|saturday|sunday";
const ORDINAL = "(?:st|nd|rd|th)?";

// [kind, pattern] in the order they are applied. Each match becomes one
// placeholder (unless it is a column name).
const VALUE_PATTERNS = [
  ["text", /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g], // emails
  ["date", /\b\d{4}-\d{1,2}-\d{1,2}(?:[T ]\d{1,2}:\d{2}(?::\d{2})?)?\b/g],
  ["date", /\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/g],
  ["date", new RegExp(`\\b\\d{1,2}${ORDINAL}\\s+(?:of\\s+)?(?:${MONTHS})\\.?(?:,?\\s+\\d{2,4})?\\b`, "gi")],
  ["date", new RegExp(`\\b(?:${MONTHS})\\.?\\s+\\d{1,2}${ORDINAL}(?:,?\\s+\\d{2,4})?\\b`, "gi")],
  ["date", new RegExp(`\\b(?:${MONTHS})\\.?\\s+\\d{4}\\b`, "gi")],
  ["date", new RegExp(`\\b(?:${FULL_MONTHS}|${WEEKDAYS})\\b`, "gi")],
  ["date", /\b\d{1,2}:\d{2}(?::\d{2})?\s?(?:[ap]\.?m\.?)?\b/gi],
  // Not a number joined to letters ("INV-00123" is one id, handled with the words).
  ["number", /(?<![\w.\-/])[-+]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?(?:st|nd|rd|th)?(?![\w\-/])/g],
];

// Any run of letters, digits and joiners: words, ids (INV-001, S1234567A),
// file names (payroll.xlsx) and words in other alphabets.
const TOKEN = /[\p{L}\p{N}_]+(?:['.\-/][\p{L}\p{N}_]+)*/gu;
const MARK = /\uE000([\uE100-\uEFFF])\uE001/g;
const PLACEHOLDER = /\{\{(text|number|date)_(\d{1,3})\}\}/g;

// Real values <-> placeholders for one conversation, so a value keeps its
// placeholder across messages.
export const createVault = () => ({ byKey: new Map(), byToken: new Map(), counters: {} });

const tokenFor = (vault, kind, value) => {
  const key = `${kind}\u0001${value.toLowerCase()}`;
  if (!vault.byKey.has(key)) {
    vault.counters[kind] = (vault.counters[kind] || 0) + 1;
    const token = `${kind}_${vault.counters[kind]}`;
    vault.byKey.set(key, token);
  }
  const token = vault.byKey.get(key);
  vault.byToken.set(token, { kind, value }); // as last typed
  return token;
};

// The value a number placeholder stands for, as a number.
const numberValue = (text) => Number(String(text).replace(/,|st$|nd$|rd$|th$/gi, ""));

// Comparison symbols become words, so "amount > 500" is plain English.
const operatorsToWords = (text) =>
  text
    .replace(/\s*>=\s*/g, " at least ")
    .replace(/\s*<=\s*/g, " at most ")
    .replace(/\s*(!=|<>)\s*/g, " not equal to ")
    .replace(/\s*={1,3}\s*/g, " equals ")
    .replace(/\s*>\s*/g, " greater than ")
    .replace(/\s*<\s*/g, " less than ");

const isVocabulary = (rules, word) => {
  const lower = word.toLowerCase();
  if (rules.vocabulary.has(lower)) return true;
  const base = lower.replace(/'s$|s'$/, "");
  return base !== lower && rules.vocabulary.has(base);
};

// True when the word starts a sentence (start of text, or after . ! ? : or a new line).
const startsSentence = (text, index) => /(^|[.!?:\n])\s*["'(]?$/.test(text.slice(0, index));

// Same check as the server's checkMessage(): what will be accepted.
const finalCheck = (rules, text, names) => {
  const rest = text.replace(PLACEHOLDER, " ");
  if (!rules.allowedChars.test(rest)) return "symbols";
  if (/[$\u20AC\u00A3\u00A5]/.test(rest.replace(/[$\u20AC\u00A3\u00A5](?= )/g, ""))) return "symbols";
  const words = rest.match(/[A-Za-z0-9_]+(?:'[A-Za-z]+)?/g) || [];
  for (const word of words) {
    if (names.has(word.toLowerCase())) continue;
    if (/\d/.test(word) || word.includes("_") || !isVocabulary(rules, word)) return "values";
  }
  return words.some((w) => isVocabulary(rules, w)) ? null : "empty";
};

// Cleans `raw` for sending. `columnNames` are the workflow's column names and
// letters; `findDataWords(words)` resolves with the (lower-case) words that
// appear in the user's loaded files. Returns
//   { error, message }                          refused (nothing is sent)
//   { text, hidden: [{ token, kind, value }] }  the text to send, values replaced
export const maskPrompt = async (raw, { rules, vault, columnNames = [], findDataWords = async () => new Set() }) => {
  const refuse = (reason, rule = reason) => ({ error: reason, rule, message: rules.messages[reason] || rules.messages.symbols });
  let text = normalize(raw);
  if (!text) return refuse("empty");
  if (text.length > rules.maxChars) return refuse("long");
  // Braces are never needed in plain English (and placeholders are ours to write).
  if (/[{}]/.test(text)) return refuse("symbols");
  const blocked = blockedRule(rules, text);
  if (blocked) return refuse(blocked.reason, blocked.rule);
  text = normalize(operatorsToWords(text));

  const names = nameWords(columnNames);
  const fullNames = new Set(columnNames.map((n) => String(n ?? "").toLowerCase()));
  const isName = (value) => fullNames.has(value.toLowerCase()) || names.has(value.toLowerCase());
  const hidden = new Map(); // token -> { token, kind, value }
  const marks = []; // index -> token
  const mark = (kind, value) => {
    const token = tokenFor(vault, kind, value);
    hidden.set(token, { token, kind, value });
    marks.push(token);
    return `\uE000${String.fromCharCode(0xe100 + marks.length - 1)}\uE001`;
  };

  // 1. Quoted text (unless it names a column).
  text = text.replace(/"([^"\n]{1,200})"/g, (all, inner) => (isName(inner.trim()) ? all : `"${mark("text", inner.trim())}"`));
  text = text.replace(/(^|[\s(])'([^'\n]{1,200})'(?=$|[\s.,;:!?)])/g, (all, before, inner) => (isName(inner.trim()) ? all : `${before}"${mark("text", inner.trim())}"`));

  // 2. Emails, dates, times and numbers.
  VALUE_PATTERNS.forEach(([kind, pattern]) => {
    text = text.replace(pattern, (value) => (isName(value) ? value : mark(kind, value)));
  });

  // 3. Words: keep column names and plain English; replace the rest.
  const candidates = [...new Set((text.match(TOKEN) || []).map((t) => t.toLowerCase()))].filter((t) => !names.has(t) && !SAFE_WORDS.has(t));
  const inData = candidates.length ? await findDataWords(candidates) : new Set();
  text = text.replace(TOKEN, (token, index, whole) => {
    const lower = token.toLowerCase();
    if (isName(token)) return token;
    // Ids, file names, other alphabets: anything with digits, joiners or non-ASCII letters.
    if (/[^A-Za-z']/.test(token)) {
      // "year-end" stays; "INV-001", "payroll.xlsx" and words in other alphabets are values.
      const parts = token.split("-");
      const plain = parts.length > 1 && parts.every((p) => /^[A-Za-z]+$/.test(p) && isVocabulary(rules, p) && !inData.has(p.toLowerCase()));
      return plain ? token : mark("text", token);
    }
    // A capitalised word mid-sentence is most likely a name ("Mark", "Will").
    if (/^[A-Z][a-z']/.test(token) && !startsSentence(whole, index) && !CAPS_OK.has(lower)) return mark("text", token);
    if (inData.has(lower) && !SAFE_WORDS.has(lower)) return mark("text", token);
    return SAFE_WORDS.has(lower) || isVocabulary(rules, token) ? token : mark("text", token);
  });

  // 4. Neighbouring replaced words are one value ("Umair Khan").
  text = text.replace(/\uE000([\uE100-\uEFFF])\uE001(?: \uE000([\uE100-\uEFFF])\uE001)+/g, (run) => {
    const tokens = [...run.matchAll(MARK)].map((m) => marks[m[1].charCodeAt(0) - 0xe100]);
    if (!tokens.every((t) => t.startsWith("text_"))) return run;
    const value = tokens.map((t) => hidden.get(t).value).join(" ");
    return mark("text", value);
  });

  // Placeholders, and only those used in the final text are listed.
  const used = new Set();
  text = text.replace(MARK, (m, code) => {
    const token = marks[code.charCodeAt(0) - 0xe100];
    used.add(token);
    return `{{${token}}}`;
  });
  const problem = finalCheck(rules, text, names);
  if (problem) return refuse(problem);
  return { text, hidden: [...used].map((token) => hidden.get(token)) };
};

// The value each placeholder stands for, typed for the step forms.
export const hiddenValue = ({ kind, value }) => (kind === "number" ? numberValue(value) : value);

// Table rows Claude proposed, with each placeholder cell ("{{text_3}}")
// replaced by the value it hides ("" when this device doesn't know it).
export const fillHiddenRows = (rows, vault) =>
  rows.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([column, cell]) => {
        const token = /^\{\{((?:text|number|date)_\d{1,3})\}\}$/.exec(cell)?.[1];
        return [column, token ? String(vault.byToken.get(token)?.value ?? "") : cell];
      }),
    ),
  );

// A column name that must not be sent: it looks like data (email, phone,
// long number) or like an instruction or code. Mirrors columnNameProblem()
// in server/ai/plainText.js.
export const columnNameProblem = (rules, name) => {
  const text = normalize(name);
  if (!text || text.length > 100) return true;
  if (/@[\w-]+\.\w|\d{6,}/.test(text)) return true;
  const phones = text.match(/\+?\d[\d\s().-]{6,}\d/g) || [];
  if (phones.some((p) => p.replace(/\D/g, "").length >= 8 && !/^(19|20)\d\d\s*[-/]\s*(19|20)\d\d$/.test(p))) return true;
  const reason = blockedReason(rules, text);
  return Boolean(reason && reason !== "system");
};

// Splits text with placeholders into parts for display: [{ text } | { token, value }].
export const splitPlaceholders = (text, vault) => {
  const parts = [];
  let last = 0;
  String(text).replace(PLACEHOLDER, (match, kind, n, index) => {
    if (index > last) parts.push({ text: text.slice(last, index) });
    const token = `${kind}_${Number(n)}`;
    const entry = vault?.byToken.get(token);
    parts.push(entry ? { token, value: entry.value } : { text: match });
    last = index + match.length;
    return match;
  });
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
};
